<template>
  <v-container fluid class="mx-4 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          hide-details="auto"
          label="Employee ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn class="accent white--text" @click="fetchDataOnTabChange">
          Search
        </v-btn>
        <v-btn
          :disabled="!reportData.length"
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="align-center mt-3">
      <!-- Center Tabs in 8 columns with offset to center -->
      <v-col cols="6" offset="4" class="d-flex justify-center">
        <v-tabs
          v-model="tab"
          @change="fetchDataOnTabChange()"
          background-color="transparent"
          class="mt-0 pt-0"
        >
          <v-tab>To Be Approved</v-tab>
          <v-tab>Approved</v-tab>
          <v-tab>Rejected</v-tab>
        </v-tabs>
      </v-col>
      <v-col cols="2" class="d-flex justify-end">
        <v-btn
          v-if="tab == 0"
          class="success white--text ml-2"
          :disabled="!selectedItems.length"
          @click="showPartnerConfirmation('Approved')"
        >
          Approve
        </v-btn>
        <v-btn
          v-if="tab == 0"
          class="error white--text ml-2"
          :disabled="!selectedItems.length"
          @click="showPartnerConfirmation('Rejected')"
        >
          Reject
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="selectedItems"
            :headers="partnerHeaders"
            :items="reportData"
            item-key="id"
            :show-select="tab === 0"
            fixed-header
            :show-select-all="true"
            :multi-sort="true"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="edit(item.id, item.program_id)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.employee_id`]="{ value }">
              {{ value ? value : "-" }}
            </template>
            <template v-slot:[`item.partner_approval_required`]="{ value }">
              {{ value ? "Yes" : "No" }}
            </template>
            <template v-slot:[`item.reviewed_user_id`]="{ item }">
              {{
                getUserName(
                  item?.reviewed_user_id ? item.reviewed_user_id : "-",
                )
              }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" fullscreen persistent no-click-animation>
      <formDialog
        v-if="dialog"
        @update="fetchReport(), (dialog = false)"
        :applicant="applicantDetail"
        :qualifications="qualifications"
      />
    </v-dialog>
    <v-dialog v-model="partnerConfirmationDialog" width="500" persistent>
      <confirmationDialogForm
        v-if="partnerConfirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="partnerConfirmationDialog = false"
        @saveDialog="updatePartnerApplicants"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import formDialog from "@/components/applicant/form.vue";
import pendingHistory from "@/components/applicant/pendingHistory.vue";
import { applicantPayload } from "@/helpers/payload";
import confirmationDialogForm from "@/components/confirmationDialog.vue";
import { rules } from "@/helpers/rules";
import { partnerReportHeaders } from "@/constants/headers";

export default {
  components: {
    formDialog,
    pendingHistory,
    confirmationDialogForm,
  },
  data() {
    return {
      form: {
        program_id: null,
        employee_id: null,
        application_id: null,
      },
      reportData: [],
      partnerHeaders: partnerReportHeaders,
      selectedItems: [],
      dialog: false,
      applicantDetail: {},
      qualifications: [],
      tab: 0,
      programs: [],
      users: [],
      currentStatus: "",
      partnerConfirmationDialog: false,
      rules,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  watch: {
    form: {
      handler() {
        this.reportData = [];
      },
      deep: true,
    },
  },
  async created() {
      this.$store.commit("setBreadcrumbs", [{ text: "Approvals" }]);
      Promise.all([this.init(), this.fetchReport(), this.getPartnerReports()]);
  },
  methods: {
    init() {
      Promise.all([this.getPrograms(), this.getUsers()]);
    },
    async edit(applicant_detail_id, program_id) {
      const [program, applicant] = await Promise.all([
        this.$axios.get(`/programs/${program_id}`),
        this.$axios.get(`/applicant_details/${applicant_detail_id}`),
      ]);
      const applicantData = applicant.data.data;
      this.qualifications = program.data.data.qualifications;
      this.applicantDetail = applicantPayload(
        this.qualifications,
        applicantData,
      );
      this.dialog = true;
    },
    async fetchReport() {
      const statusMap = {
        0: "Pending",
        1: "Approved",
        2: "Rejected",
      };

      this.form = {
        ...this.form,
        ...(statusMap[this.tab] && {
          partner_approval_status: statusMap[this.tab],
        }),
      };
      const queryString = this.objectToQueryParams(this.form);
      await this.$axios
        .get(`/applicant_details?${queryString}&!application_status=Draft`)
        .then((res) => {
          this.reportData = res.data.data;
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    objectToQueryParams(obj) {
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");
    },
    showPartnerConfirmation(status) {
      const showStatus = status === "Approved" ? "approve" : "reject";
      this.confirmationText = `Are you sure you want to ${showStatus} the selected ${
        this.selectedItems.length > 1 ? "applicants" : "applicant"
      }?`;
      this.partnerConfirmationDialog = true;
      this.currentStatus = status;
    },
    updatePartnerApplicants() {
      this.partnerConfirmationDialog = false;
      const message =
        this.selectedItems.length > 1
          ? `Applicants ${this.currentStatus} successfully`
          : `Applicant ${this.currentStatus} successfully`;
      const itemIds = this.selectedItems.map((item) => item.id);
      this.updatePartnerApprovalStatus(itemIds, this.currentStatus, message);
    },
    async updatePartnerApprovalStatus(ids, status, message) {
      this.$axios
        .put(`/update_applicant_by_partner/`, {
          partner_approval_status: status,
          ids,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message,
            color: "success",
          });
          this.selectedItems = [];
          this.fetchReport();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    getPartnerReports() {
      if (this.currentUser.role === "Partner") {
        const headers = this.partnerHeaders;
        const indexToRemove = [];
        if (this.tab === 1) {
          if (!headers.find((h) => h.value === "usn_id")) {
            headers.splice(3, 0, {
              text: "USN",
              value: "usn_id",
              class: "black--text font-weight-bold secondary",
            });
          }
          if (!headers.find((h) => h.value === "application_status")) {
            headers.splice(headers.length - 2, 0, {
              text: "Status",
              value: "application_status",
              class: "black--text font-weight-bold secondary",
            });
            headers.splice(headers.length - 1, 0, {
              text: "Partner Approved At",
              value: "partner_approved_at",
              class: "black--text font-weight-bold secondary",
            });
          }
        } else {
          indexToRemove.push(
            headers.findIndex((h) => h.value === "application_status"),
            headers.findIndex((h) => h.value === "usn_id"),
            headers.findIndex((h) => h.value === "partner_approved_at"),
          );
        }
        indexToRemove
          .filter((i) => i !== -1)
          .sort((a, b) => b - a)
          .forEach((i) => headers.splice(i, 1));
      }
    },
    fetchDataOnTabChange() {
      this.selectedItems = [];
      this.fetchReport();
      this.getPartnerReports();
      this.init();
    },
    async getUsers() {
      try {
        const response = await this.$axios.get("/users");
        this.users = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getUserName(userId) {
      const user = this.users.find((user) => user.id === userId);
      return user ? user.first_name + " " + user.last_name : "";
    },
    async getPrograms() {
      try {
        const url =
          this.currentUser.role === "Partner"
            ? `/get_partner_programs/${this.currentUser.partner_id}`
            : "/programs";
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async download() {
      try {
        let queryParams = this.objectToQueryParams(this.form);

        queryParams += "&!application_status=Draft";

        if (this.currentUser.partner_id !== 1) {
          queryParams += `&partner_id=${encodeURIComponent(
            this.currentUser.partner_id,
          )}`;
        }

        const res = await this.$axios.get(
          `/download_partner_approval?${queryParams}`,
          { responseType: "blob" },
        );

        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Partner Approval.csv");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        link.remove();
      } catch (error) {
        console.error("Download failed:", error);
      }
    },
  },
};
</script>

<style scoped></style>
